import dayjs from 'dayjs';

import { type DateRangeType } from '@/components/page';
import { DateISOFormat } from '@/constants';
import type { C99FlagSet } from '@/constants/featureFlags';

export const WEB_STORAGE = {
  LAST_VISITED_ORG: `channel99-last_visited_org-${import.meta.env.VITE_ENVIRONMENT}`,
  DEFAULT_DATE_RANGE: `channel99-default-date-range-${import.meta.env.VITE_ENVIRONMENT}`,
  TABLE_COLUMNS_STATE: (tableKey: string) =>
    `channel99-table-columns-state-${tableKey}-${import.meta.env.VITE_ENVIRONMENT}`,
  FEATURE_FLAG_OVERRIDES: `channel99-feature-flag-overrides-${import.meta.env.VITE_ENVIRONMENT}`,
  PREFERRED_SIGN_IN_METHOD: `channel99-preferred-sign-in-method-${import.meta.env.VITE_ENVIRONMENT}`,
  PREFERRED_SIGN_IN_METHOD_PENDING: `channel99-preferred-sign-in-method-pending-${import.meta.env.VITE_ENVIRONMENT}`,
  TENANT_PREFERRED_SIGN_IN_METHOD: (slug: string) =>
    `channel99-tenant-preferred-sign-in-method-${slug}-${import.meta.env.VITE_ENVIRONMENT}`,
  TENANT_PREFERRED_SIGN_IN_METHOD_PENDING: (slug: string) =>
    `channel99-tenant-preferred-sign-in-method-pending-${slug}-${import.meta.env.VITE_ENVIRONMENT}`,
} as const;

export const SIGN_IN_METHODS = ['google', 'microsoft', 'sso', 'email'] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];

const isSignInMethod = (value: string | null): value is SignInMethod => {
  return value != null && SIGN_IN_METHODS.includes(value as SignInMethod);
};

export const getPreferredSignInMethod = (): SignInMethod | null => {
  const value = localStorage.getItem(WEB_STORAGE.PREFERRED_SIGN_IN_METHOD);
  return isSignInMethod(value) ? value : null;
};

export const setPreferredSignInMethod = (method: SignInMethod) => {
  localStorage.setItem(WEB_STORAGE.PREFERRED_SIGN_IN_METHOD, method);
};

export const setPendingSignInMethod = (method: SignInMethod) => {
  localStorage.setItem(WEB_STORAGE.PREFERRED_SIGN_IN_METHOD_PENDING, method);
};

export const consumePendingSignInMethod = (): SignInMethod | null => {
  const value = localStorage.getItem(WEB_STORAGE.PREFERRED_SIGN_IN_METHOD_PENDING);
  localStorage.removeItem(WEB_STORAGE.PREFERRED_SIGN_IN_METHOD_PENDING);
  return isSignInMethod(value) ? value : null;
};

export const getTenantPreferredSignInMethod = (slug: string): SignInMethod | null => {
  const value = localStorage.getItem(WEB_STORAGE.TENANT_PREFERRED_SIGN_IN_METHOD(slug));
  return isSignInMethod(value) ? value : null;
};

export const setTenantPreferredSignInMethod = (slug: string, method: SignInMethod) => {
  localStorage.setItem(WEB_STORAGE.TENANT_PREFERRED_SIGN_IN_METHOD(slug), method);
};

export const setTenantPendingSignInMethod = (slug: string, method: SignInMethod) => {
  localStorage.setItem(WEB_STORAGE.TENANT_PREFERRED_SIGN_IN_METHOD_PENDING(slug), method);
};

export const consumeTenantPendingSignInMethod = (slug: string): SignInMethod | null => {
  const value = localStorage.getItem(WEB_STORAGE.TENANT_PREFERRED_SIGN_IN_METHOD_PENDING(slug));
  localStorage.removeItem(WEB_STORAGE.TENANT_PREFERRED_SIGN_IN_METHOD_PENDING(slug));
  return isSignInMethod(value) ? value : null;
};

export const getFeatureFlagOverrides = (): C99FlagSet =>
  JSON.parse(localStorage.getItem(WEB_STORAGE.FEATURE_FLAG_OVERRIDES) ?? `{}`) as C99FlagSet;
export const setFeatureFlagOverrides = (overrides: C99FlagSet) => {
  localStorage.setItem(WEB_STORAGE.FEATURE_FLAG_OVERRIDES, JSON.stringify(overrides));
};

const getDateRangeFromURL = () => {
  const url = new URL(window.location.href);
  const startDate = url.searchParams.get('startDate');
  const endDate = url.searchParams.get('endDate');
  if (startDate) {
    const dateRange = { startDate, endDate: endDate ?? dayjs().format(DateISOFormat) };
    sessionStorage.setItem(WEB_STORAGE.DEFAULT_DATE_RANGE, JSON.stringify(dateRange));

    // remove date range from url
    url.searchParams.delete('startDate');
    url.searchParams.delete('endDate');
    window.history.replaceState(null, '', url);

    return dateRange;
  }
  return null;
};

const getDateRangeFromSessionStorage = () => {
  const dateRange = sessionStorage.getItem(WEB_STORAGE.DEFAULT_DATE_RANGE);
  if (dateRange) {
    // TODO: drop `start_date` and `end_date` after a while. We'll need to make sure they are out
    // session storage first. If it's been a few weeks since this was push to production, it should
    // be removable.
    const parsedDateRange: DateRangeType & {
      start_date?: string;
      end_date?: string;
    } = JSON.parse(dateRange);

    // if the old snakecase `start_date` format is still in storage, reset it with camelcase format
    // TODO: Remove this `if` statement once in prod for a few weeks.
    if (parsedDateRange.start_date || parsedDateRange.end_date) {
      const reformattedDateRange = {
        startDate: parsedDateRange.start_date || parsedDateRange.startDate,
        endDate: parsedDateRange.end_date || parsedDateRange.endDate,
      };
      sessionStorage.setItem(WEB_STORAGE.DEFAULT_DATE_RANGE, JSON.stringify(reformattedDateRange));
      return reformattedDateRange;
    }

    return parsedDateRange;
  }
  return null;
};

export const getDefaultDateRange = () => {
  return getDateRangeFromURL() ?? getDateRangeFromSessionStorage();
};
export const setDefaultDateRange = (dateRange: { startDate: string; endDate: string }) =>
  sessionStorage.setItem(WEB_STORAGE.DEFAULT_DATE_RANGE, JSON.stringify(dateRange));
export const removeDefaultDateRange = () =>
  sessionStorage.removeItem(WEB_STORAGE.DEFAULT_DATE_RANGE);

export const getVisibleTableColumnsState = (tableKey: string) => {
  const columnStateStr = localStorage.getItem(WEB_STORAGE.TABLE_COLUMNS_STATE(tableKey));
  if (columnStateStr == null) {
    return null;
  }

  try {
    return JSON.parse(columnStateStr) as { sortedColumns: string[]; hiddenColumns: string[] };
  } catch {
    return null;
  }
};

export const setVisibleTableColumnsState = (
  tableKey: string,
  columnState: { sortedColumns: string[]; hiddenColumns: string[] },
) => {
  localStorage.setItem(WEB_STORAGE.TABLE_COLUMNS_STATE(tableKey), JSON.stringify(columnState));
};

export const clearSessionStorage = () => sessionStorage.clear();
